import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Printer, Menu, X, Phone, MapPin } from 'lucide-react';
// import logoImg from '../assets/logo_pink.png';
const logoImg = 'https://public.youware.com/image/4f577a9b-e4e2-4cd4-994d-ec9304f882f0/zydd7kzq51.jpeg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`relative w-full z-40 transition-all duration-300 ${
        scrolled ? 'nav-modern shadow-2xl' : 'bg-transparent'
      }`}
    >
      {/* Barre d'info contact - Masquée au scroll */}
      <div className={`bg-neutral-800 text-neutral-200 py-3 px-4 transition-all duration-300 ${
        scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'
      }`}>
        <div className="container mx-auto flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 interactive-element">
              <Phone className="w-4 h-4 text-primary-pink" />
              <span className="text-neutral-300">+2250787635341</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-primary-pink" />
              <span className="text-neutral-300">Abidjan, Côte d'Ivoire</span>
            </div>
          </div>
          <div className="hidden md:block">
            <span className="text-neutral-400">Votre imprimerie numérique de confiance</span>
          </div>
        </div>
      </div>
      <div className="w-full lg:!max-w-[1280px] lg:!mx-auto lg:!px-8 lg:!h-22 lg:!flex lg:!items-center lg:!justify-between lg:!gap-8" style={{ height: '90px', padding: '0 24px' }}>
        <div className="flex items-center justify-between h-full w-full">
          {/* Logo + Text - Zone gauche */}
          <div className="lg:!flex lg:!items-center lg:!gap-3">
            <Link to="/" className="flex items-center interactive-element hover:opacity-80 transition-opacity lg:!flex lg:!items-center lg:!gap-3" style={{ gap: '12px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <div className="rounded-2xl shadow-lg flex-shrink-0">
                <img 
                  src={logoImg} 
                  alt="DigiPrint Logo" 
                  className="object-contain"
                  style={{ maxWidth: '180px', maxHeight: '70px', width: 'auto', height: 'auto' }}
                />
              </div>
              <div className="flex flex-col justify-center lg:!flex lg:!flex-col lg:!justify-center">
                <h1 className="font-bold text-primary-pink lg:!text-xl lg:!font-bold lg:!text-primary-pink lg:!m-0" style={{ fontSize: '20px', lineHeight: '1.2', margin: 0 }}>DigiPrint</h1>
                <p className="text-neutral-500 font-medium lg:!text-base lg:!font-medium lg:!text-neutral-500 lg:!m-0 lg:!mt-0.5" style={{ fontSize: '16px', lineHeight: '1.2', margin: 0, marginTop: '2px' }}>Numérique</p>
              </div>
            </Link>
          </div>

          {/* Navigation Desktop - Zone centre */}
          <div className="hidden md:flex items-center lg:!flex lg:!items-center">
            <nav className="flex items-center lg:!flex lg:!items-center lg:!gap-8">
              {[
                { path: '/', label: 'Accueil' },
                { path: '/services', label: 'Services' },
                { path: '/gallery', label: 'Réalisations' },
                { path: '/about', label: 'À propos' },
                { path: '/contact', label: 'Contact' }
              ].map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''} fade-in stagger-${index + 1} lg:!m-0 lg:!p-0 lg:!text-base lg:!leading-6`}
                  style={{ fontSize: '18px', whiteSpace: 'nowrap' }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* CTA Buttons - Zone droite */}
          <div className="hidden md:flex items-center lg:!flex lg:!items-center lg:!gap-4">
            <a
              href="https://wa.me/2250787635341"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent px-6 py-3 text-sm flex items-center justify-center lg:!px-5 lg:!py-3 lg:!text-sm lg:!flex lg:!items-center lg:!justify-center lg:!h-11 lg:!min-w-32"
              style={{ height: '44px', minWidth: '120px' }}
            >
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="btn-primary px-6 py-3 text-sm flex items-center justify-center lg:!px-5 lg:!py-3 lg:!text-sm lg:!flex lg:!items-center lg:!justify-center lg:!h-12 lg:!min-w-36"
              style={{ height: '44px', minWidth: '140px' }}
            >
              Devis gratuit
            </Link>
          </div>

          {/* Mobile Menu Button - Right aligned on mobile */}
          <button
            onClick={toggleMenu}
            className="md:hidden text-neutral-700 hover:text-primary-black interactive-element p-2 ml-auto"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu - Modern Sliding Panel */}
        <div className={`md:hidden fixed inset-0 z-50 transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={toggleMenu}
          />
          
          {/* Menu Panel */}
          <div className={`absolute right-0 top-0 h-full w-80 max-w-[85vw] surface-elevated transform transition-transform duration-500 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
            <div className="p-8">
              {/* Close button */}
              <button
                onClick={toggleMenu}
                className="absolute top-6 right-6 text-neutral-600 hover:text-primary-black interactive-element"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Logo */}
              <div className="flex items-center space-x-3 mb-8">
                <div className="rounded-xl">
                  <img 
                    src={logoImg} 
                    alt="DigiPrint Logo" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-neutral-800">DigiPrint</h2>
                  <p className="text-xs text-neutral-500">Numérique</p>
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex flex-col space-y-6 mb-8">
                {[
                  { path: '/', label: 'Accueil' },
                  { path: '/services', label: 'Services' },
                  { path: '/gallery', label: 'Réalisations' },
                  { path: '/about', label: 'À propos' },
                  { path: '/contact', label: 'Contact' }
                ].map((item, index) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-lg font-medium transition-colors duration-300 ${
                      isActive(item.path) 
                        ? 'text-primary-black' 
                        : 'text-neutral-700 hover:text-primary-black'
                    } fade-in stagger-${index + 1}`}
                    onClick={toggleMenu}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              {/* Mobile CTA Buttons */}
              <div className="flex flex-col space-y-4">
                <a
                  href="https://wa.me/2250787635341"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-accent text-center py-4 rounded-2xl"
                >
                  WhatsApp Direct
                </a>
                <Link
                  to="/contact"
                  className="btn-primary text-center py-4 rounded-2xl"
                  onClick={toggleMenu}
                >
                  Demander un devis
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;